import { useId, useState, type FormEvent } from 'react';
import { fieldStyle, StatusDot, token } from '@bp-files/ui';
import { messageFrom } from '@bp-files/client';
import { resendPasscode, startPasscode, verifyPasscode } from '@/lib/api';
import { SIGN_IN_LANDING_COPY } from './signInLanding.copy';

/**
 * The SYS-7 passcode, as two fields on this page (ADR-0051's rule, extended).
 *
 * **This replaces a redirect to a Microsoft page**, which is the whole of the
 * onboarding change: an invited member who has never signed up used to have to
 * be told, by a person, to go and create an account somewhere else. Now they
 * type their address and a code, and the API provisions them in between without
 * ever saying so.
 *
 * **The page still has one control, and it still names its own act.** That is
 * SYS-8's rule and the thing four rewrites of this page kept breaking. What
 * changed is that two of the acts now have a field in front of them — so each
 * step carries its own label, its own button word, and its own pending word,
 * and there is never a second control competing with the first. The two
 * secondary affordances on the code step (resend, and go back) are spaces under
 * the button rather than buttons beside it, for the same reason.
 *
 * **It calls `lib/api` directly rather than taking injected functions.** The
 * rest of this page is injected because it drives *credentials* — an
 * authenticator, an MSAL redirect — which a test cannot perform. Three POSTs are
 * not that: MSW covers them at the network boundary, which is this workspace's
 * rule for everything that is merely HTTP.
 */

type Step =
  | { readonly kind: 'email' }
  | {
      readonly kind: 'code';
      /** What they typed. Echoed back, rather than Entra's masked form. */
      readonly email: string;
      /** Opaque handle to the server-side flow. Never persisted. */
      readonly flow: string;
      readonly codeLength: number;
    };

const FIELD_CLASS = 'w-full border px-3 py-2 text-base';

const BUTTON_CLASS =
  'inline-flex min-h-11 min-w-56 items-center justify-center rounded-md border px-8 text-base font-semibold transition-colors outline-none focus-visible:outline-2 disabled:cursor-not-allowed disabled:opacity-70';

function buttonStyle() {
  return {
    // `bp-blue-text` is under 4.5:1 on this fill — the deep blue is the one that
    // passes, and it is what every other primary control on the page uses.
    borderColor: token('bp-blue'),
    background: token('bp-blue-deep'),
    color: token('bp-on-accent'),
  };
}

const LINK_CLASS =
  'rounded-md px-2 py-1.5 text-sm font-medium text-secondary underline underline-offset-4 transition-colors hover:text-body disabled:opacity-50';

export interface PasscodeFormProps {
  /**
   * Re-run the session check now that a cookie exists.
   *
   * **Not `onReady`, deliberately.** Every path that obtains a session ends by
   * asking `/me` again, because `resolveSession`'s `settle` is where the passkey
   * hint is taught and un-taught and where `registerSession` runs. A form that
   * handed its own response to the app would skip all of it, and the hint rules
   * would rot the first time somebody signed in this way.
   */
  readonly onSignedIn: () => Promise<void>;
}

export function PasscodeForm({ onSignedIn }: PasscodeFormProps) {
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [resent, setResent] = useState(false);

  const fieldId = useId();
  const hintId = `${fieldId}-hint`;
  const copy = SIGN_IN_LANDING_COPY.passcode;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;

    setResent(false);
    setError(undefined);

    if (step.kind === 'email') {
      const address = email.trim();
      // Client-side as a courtesy only — the API decides who has access, and a
      // shape check here just saves a round trip on an obvious typo.
      if (!address.includes('@')) {
        setError(copy.email.invalid);
        return;
      }
      void send(address);
      return;
    }

    const entered = code.trim();
    if (entered === '') {
      setError(copy.code.invalid);
      return;
    }
    void check(step.flow, entered);
  }

  async function send(address: string): Promise<void> {
    setPending(true);
    try {
      const sent = await startPasscode(address);
      setStep({ kind: 'code', email: address, flow: sent.flow, codeLength: sent.codeLength });
      setCode('');
    } catch (caught) {
      // The API's own sentence, not ours: an unprovisioned address is told to
      // talk to its agency administrator, and that wording is SYS-9's to own.
      setError(messageFrom(caught, copy.fallback));
    } finally {
      setPending(false);
    }
  }

  async function check(flow: string, entered: string): Promise<void> {
    setPending(true);
    try {
      await verifyPasscode(flow, entered);
      // A session exists now. Everything about what it is worth — restricted or
      // not, named or not — comes from the check, not from here.
      await onSignedIn();
    } catch (caught) {
      setError(messageFrom(caught, copy.fallback));
      setPending(false);
    }
  }

  function again(): void {
    if (pending || step.kind !== 'code') return;

    setError(undefined);
    setPending(true);
    void resendPasscode(step.flow)
      .then(() => {
        setResent(true);
      })
      .catch((caught: unknown) => {
        setError(messageFrom(caught, copy.fallback));
      })
      .finally(() => {
        setPending(false);
      });
  }

  function startOver(): void {
    if (pending) return;
    setError(undefined);
    setResent(false);
    setCode('');
    setStep({ kind: 'email' });
  }

  const onCode = step.kind === 'code';
  const field = onCode ? copy.code : copy.email;

  return (
    // **`noValidate`, so our message is the one anybody sees.** `type="email"`
    // still earns the right keyboard on a phone, but its native bubble is
    // unstyled, inconsistently announced, and not in `signInLanding.copy.ts` —
    // and this page's errors are `role="alert"` beside the field, everywhere
    // else. Validation still happens; it happens in the submit handler.
    <form onSubmit={submit} noValidate className="max-w-md">
      <label htmlFor={fieldId} className="mb-2 block text-sm font-medium text-body">
        {onCode ? copy.code.label(step.email) : copy.email.label}
      </label>

      {onCode ? (
        <input
          id={fieldId}
          key="code"
          name="code"
          type="text"
          inputMode="numeric"
          // What makes iOS and Android offer the code straight from the
          // notification, which is most of why this step is bearable on a phone.
          autoComplete="one-time-code"
          maxLength={step.codeLength}
          value={code}
          autoFocus
          onChange={(event) => {
            setCode(event.target.value);
          }}
          aria-describedby={hintId}
          className={FIELD_CLASS}
          style={fieldStyle()}
        />
      ) : (
        <input
          id={fieldId}
          key="email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          autoFocus
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          aria-describedby={hintId}
          className={FIELD_CLASS}
          style={fieldStyle()}
        />
      )}

      <p id={hintId} className="mt-2 mb-5 text-sm text-muted">
        {field.hint}
      </p>

      {error === undefined ? null : (
        <p role="alert" className="mb-5 flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {error}
        </p>
      )}

      {resent ? (
        <p role="status" className="mb-5 text-sm text-muted">
          {copy.code.resent}
        </p>
      ) : null}

      <button type="submit" disabled={pending} className={BUTTON_CLASS} style={buttonStyle()}>
        {pending ? field.pending : field.submit}
      </button>

      {onCode ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={again} disabled={pending} className={LINK_CLASS}>
            {copy.code.resend}
          </button>
          <button type="button" onClick={startOver} disabled={pending} className={LINK_CLASS}>
            {copy.code.change}
          </button>
        </div>
      ) : null}
    </form>
  );
}
